import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { Users, Settings, LogOut, Mail, PhoneCall, Cake, School, BookOpenCheck, MapPinHouse, X, Loader2, User } from 'lucide-react'
import { Avatar } from '../Avartar'
import { useAuth } from '@hooks/useAuth'
import { useOnClickOutside } from "usehooks-ts";
import { useTranslation } from 'react-i18next';
import { useCommon } from '@hooks/useCommon'
import { useClass } from '@hooks/useClasses';
import { useAdminDashboard } from '@hooks/useAdminDashboard';
import Drawer from 'antd/es/drawer';
import dayjs from 'dayjs';
import blankavt from '@assets/images/blankavt.jpg'
import avtmale from '@assets/images/avtmale.jpg';
import avtfemale from '@assets/images/avtfemale.jpg'
import avtadmin from '@assets/images/avtadmin.jpg'
import avtgvmale from '@assets/images/avtgvmale.jpg'
import avtgvfemale from '@assets/images/avtgvfemale.jpg'
import notification from "antd/es/notification";
import { performSsoLogout } from '@hooks/useSessionWatcher';
type UserProfileProps = {
  imageUrl?: string,
  avatarSize?: 'sm' | 'md' | 'lg',
  avatarClassName?: string,
  userName: string,
  userRole: string,
}

export function UserProfile({ imageUrl, avatarSize, avatarClassName, userName, userRole }: UserProfileProps) {
  const { t } = useTranslation();
  const [isShowUserProfile, setIsShowUserProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { logout, auth, isTeacher, isStudent } = useAuth();
  const isK12Teacher = isTeacher && Boolean(auth?.k12_username);
  const isK12Student = isStudent && Boolean(auth?.k12_username);
  const hideLogout = isK12Teacher || isK12Student;
  const { fetchClassData } = useClass();
  const { updateAdminDashboardFilterSelected, adminDashboardFilterSelected } = useAdminDashboard();
  const userProfileRef = useRef(null);
  const { fetchUserDetailAction, userDetail, loadingUserDetail } = useCommon();
  const navigate = useNavigate();
  const handleLogout = async () => {
    setIsLoggingOut(true);
    // Clear the backend session record so the next login won't see a false conflict
    try {
      const authenticatedUser = JSON.parse(localStorage.getItem('authenticated_user') || 'null');
      if (authenticatedUser?.access_token) {
        await fetch(`${process.env.REACT_APP_API_URL}/api-clear-session/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authenticatedUser.access_token}`,
          },
        });
      }
    } catch {
      // best effort — don't block logout if this fails
    }
    await performSsoLogout();
    logout();
  };

  const handleClickUserProfile = () => {
    setIsShowUserProfile(!isShowUserProfile)
  }

  useOnClickOutside(userProfileRef, () => {
    setIsShowUserProfile(false)
  })
  useEffect(() => {
    fetchUserDetailAction(auth.user_id);
  }, [auth?.user_id]);
  useEffect(() => {
    const classInfo = userDetail?.classes?.[0];
    if (!classInfo) return;

    const newFilter = {
      ...adminDashboardFilterSelected,
      province: classInfo.school_province,
      district: classInfo.school_district,
      ward: classInfo.school_ward,
      school_ids: classInfo.school_id,
    };
    updateAdminDashboardFilterSelected(newFilter);

    fetchClassData({
      province: classInfo.school_province,
      district: classInfo.school_district,
      ward: classInfo.school_ward,
      school: classInfo.school_id,
    });
  }, [userDetail]);
  const getFinalAvatarUrl = () => {
    const url = userDetail?.avatar_url?.trim();
    const role = userDetail?.role_name;
    const genre = userDetail?.genre;

    if (url) return url;

    if (role === 'Học sinh') {
      if (genre === 'M') return avtmale;
      if (genre === 'F') return avtfemale;
      if (genre === 'O') return blankavt;
      return blankavt;
    }

    if (role === 'Giáo viên') {
      if (genre === 'M') return avtgvmale;
      if (genre === 'F') return avtgvfemale;
      if (genre === 'O') return blankavt;
    }
    if (role === 'Admin LMS' || 'Admin Trường học') {
      if (genre === 'O') return blankavt;
      return avtadmin;
    }
  };


  const [isAllClassesModalOpen, setIsAllClassesModalOpen] = useState(false);
  const [hiddenLogoutVisible, setHiddenLogoutVisible] = useState(false);
  const hiddenClickCountRef = useRef(0);
  const hiddenClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHiddenTripleClick = () => {
    hiddenClickCountRef.current += 1;
    if (hiddenClickTimerRef.current) clearTimeout(hiddenClickTimerRef.current);
    hiddenClickTimerRef.current = setTimeout(() => {
      hiddenClickCountRef.current = 0;
    }, 600);
    if (hiddenClickCountRef.current >= 3) {
      hiddenClickCountRef.current = 0;
      setHiddenLogoutVisible((prev) => !prev);
    }
  };

  const renderUserRoleInfo = () => {
    const classes = userDetail?.classes;

    if (!userDetail?.role_code || !Array.isArray(classes) || classes.length === 0) {
      return null;
    }

    const schoolName = classes[0]?.school_name;
    const reversedClasses = [...classes].reverse();
    const visibleClasses = reversedClasses.slice(0, 3);
    const hiddenCount = reversedClasses.length - visibleClasses.length;

    return (
      <div className="text-sm text-gray-800 mb-2 space-y-6">
        {schoolName && (
          <div className="flex items-center text-gray-800 text-sm">
            <School size={18} className='text-primary mr-2 flex-shrink-0' />
            <span className="text-gray-600">{schoolName}</span>
          </div>
        )}
        {reversedClasses.length > 0 && (
          <div className="flex items-start text-gray-800 text-sm">
            <BookOpenCheck size={18} className='text-primary mr-2 flex-shrink-0 mt-0.5' />
            <div className="min-w-0">
              <span className="text-gray-600">
                {visibleClasses.map((cls) => cls.class_name).join('; ')}
              </span>
              {hiddenCount > 0 && (
                <button
                  title={`Xem thêm ${hiddenCount} lớp`}
                  onClick={() => setIsAllClassesModalOpen(true)}
                  className="inline text-xs text-gray-400 hover:text-primary transition-colors ml-1"
                >
                  ···&nbsp;+{hiddenCount} lớp khác
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-800">Đang đăng xuất...</p>
              <p className="text-sm text-gray-600">Vui lòng chờ trong giây lát</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex" onClick={handleClickUserProfile}>
        <Avatar imageUrl={getFinalAvatarUrl()} size={avatarSize} className={avatarClassName} />
        <div className="ml-2 mt-[-5px]">
          <div className="text-[14px]">{userDetail?.first_name} {userDetail?.last_name}</div>
          <div>
            <span className="text-xs italic">{userDetail?.role_name}</span>
          </div>
        </div>
      </div>
      <div>
        <Drawer
          title={
            <div className="flex justify-between items-center w-full pr-4">
              <div className="text-base font-semibold">{t('common.userprofile')}</div>
              <X
                onClick={() => setIsShowUserProfile(false)}
                className="text-gray-500 mr-[-20px] cursor-pointer "
              >
              </X>
            </div>
          }
          placement="right"
          closable={false}
          onClose={() => setIsShowUserProfile(false)}
          open={isShowUserProfile}
        >
          <div className="mt-[18px] mb-4 flex flex-col items-center gap-2 font-sans">
            <div onClick={hideLogout ? handleHiddenTripleClick : undefined} style={hideLogout ? { cursor: 'default' } : undefined}>
              <Avatar
                imageUrl={getFinalAvatarUrl()}
                size="lg"
                className="w-[170px] h-[170px]"
              />
            </div>
            <div className="flex flex-col items-center text-center gap-1">
              <p
                className="font-semibold text-lg"
                onClick={hideLogout ? handleHiddenTripleClick : undefined}
                // title={hideLogout ? 'Nhấn 3 lần để hiện tùy chọn đăng xuất' : undefined}
                style={hideLogout ? { cursor: 'default', userSelect: 'none' } : undefined}
              >
                {userDetail?.first_name} {userDetail?.last_name}
              </p>
              <p className="text-gray-500 text-sm mb-1">{userDetail?.role_name}</p>
              {userDetail?.email && !isK12Teacher && !isK12Student && (
                <a
                  href={`mailto:${userDetail.email}`}
                  className="text-gray-600 text-sm flex items-center gap-1 mb-2 cursor-pointer hover:underline"
                >
                  <Mail size={18} className="text-primary mr-2" />
                  <span className="ml-[-4px]">{userDetail.email}</span>
                </a>
              )}
              {auth?.k12_username && (
                <p className="text-blue-600 text-xs bg-blue-50 px-2 py-0.5 rounded inline-block mb-1">
                  K12 Username : {auth.k12_username}
                </p>
              )}
            </div>
          </div>

          {(userDetail?.phone && userDetail?.birth_date) ? (
            <div className="flex items-center mb-6">
              <div className="flex w-full max-w-3xl justify-between">
                {/* Phone */}
                <div className="w-1/3 flex justify-center items-center text-gray-800 text-sm">
                  {userDetail?.phone?.trim() && (
                    <>
                      <PhoneCall size={18} className="text-primary mr-2" />
                      <span>{userDetail.phone}</span>
                    </>
                  )}
                </div>

                {/* Genre */}
                <div className="w-1/3 flex justify-center items-center text-gray-800 text-sm">
                  {userDetail?.genre && (
                    <>
                      <Users size={18} className="text-primary mr-2" />
                      <span>
                        {userDetail.genre === 'M'
                          ? t('common.male')
                          : userDetail.genre === 'F'
                            ? t('common.female')
                            : t('common.other')}
                      </span>
                    </>
                  )}
                </div>

                {/* Birth date */}
                <div className="w-1/3 flex justify-center items-center text-gray-800 text-sm">
                  {userDetail?.birth_date && (
                    <>
                      <Cake size={18} className="text-primary mr-2" />
                      <span>{dayjs(userDetail.birth_date).format('DD/MM/YYYY')}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='ml-[10px] space-y-6 mb-6'>
              {userDetail?.genre && (
                <div className="flex items-center text-gray-800 text-sm mb-2">
                  <Users size={18} className="text-primary mr-2" />
                  <span>
                    {userDetail.genre === 'M'
                      ? t('common.male')
                      : userDetail.genre === 'F'
                        ? t('common.female')
                        : t('common.other')}
                  </span>
                </div>
              )}
              {userDetail?.phone?.trim() && (
                <div className="flex items-center text-gray-800 text-sm mb-2">
                  <PhoneCall size={18} className="text-primary mr-2" />
                  <span>{userDetail.phone}</span>
                </div>
              )}

              {userDetail?.birth_date && (
                <div className="flex items-center text-gray-800 text-sm mb-2">
                  <Cake size={18} className="text-primary mr-2" />
                  <span>{dayjs(userDetail.birth_date).format('DD/MM/YYYY')}</span>
                </div>
              )}
            </div>
          )}

          <div className="ml-[10px] space-y-6">
            {renderUserRoleInfo()}

            {/* {userDetail?.wards?.district?.province && (
              <p className="flex items-center text-gray-800 text-sm mb-2">
                <div className="font-medium text-gray-700">
                  <MapPinHouse size={18} className="text-primary mr-2" />
                </div>
                <span className="text-gray-600">
                  {[
                    userDetail?.wards.full_name,
                    userDetail?.wards.district.full_name,
                    userDetail?.wards.district.province.full_name,
                  ].join(', ')}
                </span>
              </p>
            )} */}
          </div>

          <div>
            <hr className="my-7" />
            <ul className="mt-[-14px] space-y-2">
              {userDetail?.role === '4' && (
                <li
                  className="px-4 py-4 rounded flex items-start gap-3 cursor-pointer hover:bg-[#f2f2f2]"
                  onClick={() => {
                    setIsShowUserProfile(false);
                    navigate('/student/profile');
                  }}
                >
                  <User className="w-5 h-5" />
                  <div>
                    <div className="font-semibold text-sm">
                      {t('common.profile') || 'Hồ sơ'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Xem và chỉnh sửa thông tin cá nhân
                    </div>
                  </div>
                </li>
              )}
              {/* Ẩn nút Đăng xuất với giáo viên và học sinh K12 */}
              {!hideLogout && (
                <li
                  className={`px-4 py-4 rounded flex items-start gap-3 cursor-pointer ${isLoggingOut
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[#f2f2f2]'
                    }`}
                  onClick={isLoggingOut ? undefined : handleLogout}
                >
                  {isLoggingOut ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <LogOut className="w-5 h-5" />
                  )}
                  <div>
                    <div className="font-semibold text-sm">
                      {isLoggingOut ? 'Đang đăng xuất...' : t('common.logout')}
                    </div>
                  </div>
                </li>
              )}
              {hideLogout && hiddenLogoutVisible && (
                <li className="px-4 py-3 rounded border border-dashed border-orange-300 bg-orange-50">
                  <button
                    disabled={isLoggingOut}
                    onClick={isLoggingOut ? undefined : handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoggingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Đang đăng xuất...
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4" />
                        Đăng xuất
                      </>
                    )}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </Drawer>
      </div>

      {/* All Classes Modal */}
      {isAllClassesModalOpen && (() => {
        const classes = userDetail?.classes;
        if (!Array.isArray(classes)) return null;
        const reversedClasses = [...classes].reverse();
        return (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
            onClick={() => setIsAllClassesModalOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-[320px] max-h-[480px] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <span className="font-semibold text-sm text-gray-800">Danh sách lớp học</span>
                <button
                  onClick={() => setIsAllClassesModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto px-4 py-3 space-y-1">
                {reversedClasses.map((cls, idx) => (
                  <div key={idx} className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{cls.class_name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  )
}